/**
 * AI Agents screen (M7) — the agent-governance console (SRS §9).
 *
 * The operator's control surface over the five autonomous agents. It makes the
 * governance model visible and directly operable, layered top-down:
 *   1. Kill-switch banner (FR-AI.5) — the tenant-wide halt. Shows the current
 *      state loudly and toggles it; when engaged it explains that autonomous
 *      OUTBOUND action is halted while DRAFT generation is retained.
 *   2. Agents table (FR-AI.4, §9.1/§9.3) — one row per catalogued agent merged
 *      with its effective governance state: autonomy (a select), enabled (a
 *      toggle), the confidence floor, and the registered tool chips. A caption
 *      states that prohibitions are enforced STRUCTURALLY: an agent can only
 *      ever use its registered tools (§9.3).
 *   3. Dispatch tester (§9.5 eval harness) — try the guardrails live: pick an
 *      agent, an action, a confidence, and whether the input contains guest
 *      content; the returned decision (acted/drafted/escalated/refused) and its
 *      reason are shown, and the action log refreshes.
 *   4. Prompt-injection screener (§9.4/FR-AI.7) — screen arbitrary text; guest
 *      content is treated as DATA, never instructions.
 *   5. Action log (FR-AI.1) — the immutable audit surface of recent actions.
 *
 * All backend access goes through the fetch-based `../services.js` layer (web
 * boundary: no backend runtime import). API error envelopes are surfaced
 * through the shared accessible {@link UserMessage} component.
 *
 * SSR-safe: the initial render is a pure loading state; every network call and
 * any `Date` read runs inside an effect or an event handler, so the server
 * render matches hydration.
 */
import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import {
  apiAgentActions,
  apiAgentConfigs,
  apiAgentDispatch,
  apiAgentKillSwitch,
  apiAgentScreen,
  apiAgentSetAutonomy,
  asError,
  type AgentAction,
  type AgentCatalogueEntry,
  type AgentConfigs,
  type AgentDecision,
  type AgentGovernanceState,
  type AutonomyLevel,
  type DispatchResult,
  type InjectionScreen,
} from "../services.js";

/** The three autonomy levels, in escalating order (§9.1). */
const AUTONOMY_LEVELS: readonly AutonomyLevel[] = [
  "draft",
  "supervised",
  "auto",
];

/** A default effective state when an agent has no resolved state yet. */
const DEFAULT_STATE: AgentGovernanceState = {
  enabled: true,
  autonomyLevel: "draft",
  killSwitch: false,
  confidenceThreshold: 70,
};

/** A couple of FR-AI.3 prohibited actions, offered as datalist hints. */
const PROHIBITED_HINTS: readonly string[] = [
  "issue_refund",
  "override_rate",
  "modify_policy",
  "offer_discount",
  "make_commitment",
];

/** Map a dispatch decision to its badge modifier class. */
function decisionBadgeClass(decision: AgentDecision): string {
  return `agent-decision agent-decision--${decision}`;
}

/** Format an ISO timestamp for compact display (date + time), guarded. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/**
 * Resolve the effective governance state for an agent: prefer the API-resolved
 * `states[name]`, else fall back to the persisted config, else defaults.
 */
function effectiveState(
  data: AgentConfigs,
  agentName: string,
): AgentGovernanceState {
  const resolved = data.states[agentName];
  if (resolved !== undefined) {
    return resolved;
  }
  const config = data.configs.find((c) => c.agent === agentName);
  if (config !== undefined) {
    return {
      enabled: config.enabled,
      autonomyLevel: config.autonomyLevel,
      killSwitch: config.killSwitch,
      confidenceThreshold: config.confidenceThreshold,
    };
  }
  return DEFAULT_STATE;
}

/** Whether the tenant kill switch is engaged, from any config or state. */
function isKillSwitchEngaged(data: AgentConfigs): boolean {
  if (data.configs.some((c) => c.killSwitch)) {
    return true;
  }
  return Object.values(data.states).some((s) => s.killSwitch);
}

// ===========================================================================
// Kill-switch banner (FR-AI.5)
// ===========================================================================

function KillSwitchBanner({
  engaged,
  toggling,
  onToggle,
}: {
  readonly engaged: boolean;
  readonly toggling: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  return (
    <section
      className={
        engaged
          ? "agent-killbanner agent-killbanner--engaged"
          : "agent-killbanner"
      }
      aria-label="Tenant kill switch"
    >
      <div className="agent-killbanner__body">
        <p className="agent-killbanner__title">
          Kill switch{" "}
          <span
            className={
              engaged
                ? "agent-killstate agent-killstate--on"
                : "agent-killstate agent-killstate--off"
            }
          >
            {engaged ? "engaged" : "released"}
          </span>
        </p>
        <p className="agent-killbanner__note">
          {engaged
            ? "Autonomous outbound action is halted tenant-wide. Draft generation is retained."
            : "Agents may act within their autonomy level and confidence thresholds. Engage the kill switch to halt all autonomous outbound action."}
        </p>
      </div>
      <button
        type="button"
        className={engaged ? "console-btn console-btn--ghost" : "console-btn"}
        disabled={toggling}
        onClick={onToggle}
      >
        {toggling
          ? "Working…"
          : engaged
            ? "Release kill switch"
            : "Engage kill switch"}
      </button>
    </section>
  );
}

// ===========================================================================
// Agents table (FR-AI.4, §9.1/§9.3)
// ===========================================================================

function AgentRow({
  agent,
  state,
  onAutonomyChange,
  onToggleEnabled,
  actingId,
}: {
  readonly agent: AgentCatalogueEntry;
  readonly state: AgentGovernanceState;
  readonly onAutonomyChange: (agent: string, level: AutonomyLevel) => void;
  readonly onToggleEnabled: (
    agent: string,
    level: AutonomyLevel,
    nextEnabled: boolean,
  ) => void;
  readonly actingId: string | null;
}): ReactElement {
  const isActing = actingId === agent.name;
  const selectId = `agent-autonomy-${agent.name}`;
  return (
    <tr>
      <td>
        <span className="channel-name">{agent.name}</span>
      </td>
      <td>
        <label className="console-sr-only" htmlFor={selectId}>
          Autonomy level for {agent.name}
        </label>
        <select
          id={selectId}
          value={state.autonomyLevel}
          disabled={isActing}
          onChange={(e) =>
            onAutonomyChange(agent.name, e.target.value as AutonomyLevel)
          }
        >
          {AUTONOMY_LEVELS.map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </select>
      </td>
      <td>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          disabled={isActing}
          onClick={() =>
            onToggleEnabled(agent.name, state.autonomyLevel, !state.enabled)
          }
        >
          <span
            className={
              state.enabled
                ? "agent-enabled agent-enabled--on"
                : "agent-enabled agent-enabled--off"
            }
          >
            {state.enabled ? "enabled" : "disabled"}
          </span>
        </button>
      </td>
      <td className="agent-num">{agent.maxConfidenceToAct}</td>
      <td>
        {agent.tools.length > 0 ? (
          <span className="agent-tools">
            {agent.tools.map((tool) => (
              <span key={tool} className="agent-tool">
                {tool}
              </span>
            ))}
          </span>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

function AgentsPanel({
  data,
  onAutonomyChange,
  onToggleEnabled,
  actingId,
}: {
  readonly data: AgentConfigs;
  readonly onAutonomyChange: (agent: string, level: AutonomyLevel) => void;
  readonly onToggleEnabled: (
    agent: string,
    level: AutonomyLevel,
    nextEnabled: boolean,
  ) => void;
  readonly actingId: string | null;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Agents">
      <h2 className="console-panel__title">Agents</h2>
      <p className="recon-caption">
        Prohibitions are enforced structurally — an agent can only ever use its
        registered tools (§9.3). Autonomy sets how far an agent may go on its
        own; the confidence floor is the level below which it escalates instead
        of acting.
      </p>
      {data.agents.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">
            Agents with autonomy, enabled state, confidence floor, and
            registered tools
          </caption>
          <thead>
            <tr>
              <th scope="col">Agent</th>
              <th scope="col">Autonomy</th>
              <th scope="col">Enabled</th>
              <th scope="col">Confidence floor</th>
              <th scope="col">Tools</th>
            </tr>
          </thead>
          <tbody>
            {data.agents.map((agent) => (
              <AgentRow
                key={agent.name}
                agent={agent}
                state={effectiveState(data, agent.name)}
                onAutonomyChange={onAutonomyChange}
                onToggleEnabled={onToggleEnabled}
                actingId={actingId}
              />
            ))}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">No agents in the catalogue.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Dispatch tester (§9.5 eval harness)
// ===========================================================================

function DispatchPanel({
  agents,
  onDispatched,
}: {
  readonly agents: readonly AgentCatalogueEntry[];
  readonly onDispatched: () => void;
}): ReactElement {
  const [agent, setAgent] = useState("");
  const [action, setAction] = useState("");
  const [confidence, setConfidence] = useState(80);
  const [guestContent, setGuestContent] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [result, setResult] = useState<DispatchResult | null>(null);

  const selected = agents.find((a) => a.name === agent) ?? null;
  const toolOptions = selected?.tools ?? [];

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setApiError(null);
    setResult(null);
    if (agent === "") {
      setApiError({
        what: "No agent selected.",
        why: "A dispatch is evaluated against a specific agent's guardrails.",
        next: "Pick an agent and try again.",
      });
      return;
    }
    if (action.trim() === "") {
      setApiError({
        what: "No action entered.",
        why: "The eval harness needs an action to evaluate.",
        next: "Enter an action (e.g. a registered tool name) and try again.",
      });
      return;
    }
    setDispatching(true);
    try {
      const res = await apiAgentDispatch({
        agent,
        action: action.trim(),
        confidence,
        containsGuestContent: guestContent,
      });
      const err = asError(res);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      setResult(res as DispatchResult);
      // The dispatch is recorded in the action log; refresh it.
      onDispatched();
    } catch {
      setApiError({
        what: "Could not dispatch the action.",
        why: "The request to the agent service failed.",
        next: "Check the API server is running and try again.",
      });
    } finally {
      setDispatching(false);
    }
  }

  return (
    <section className="console-panel agent-dispatch" aria-label="Dispatch tester">
      <h2 className="console-panel__title">Dispatch tester</h2>
      <p className="recon-caption">
        Try the guardrails live (§9.5 eval harness). See exactly how the agent
        acts, drafts, escalates, or refuses for a given action, confidence, and
        input — including kill-switch and structural-prohibition behavior.
      </p>
      {apiError !== null ? (
        <UserMessage message={apiError} severity="error" />
      ) : null}
      <form className="console-form" onSubmit={handleSubmit} noValidate>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="dispatch-agent">Agent</label>
            <select
              id="dispatch-agent"
              value={agent}
              onChange={(e) => {
                setAgent(e.target.value);
                setResult(null);
              }}
            >
              <option value="">Select an agent…</option>
              {agents.map((a) => (
                <option key={a.name} value={a.name}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div className="console-field">
            <label htmlFor="dispatch-action">Action</label>
            <input
              id="dispatch-action"
              list="dispatch-action-options"
              placeholder="e.g. draft_reply or issue_refund"
              value={action}
              onChange={(e) => setAction(e.target.value)}
            />
            <datalist id="dispatch-action-options">
              {toolOptions.map((tool) => (
                <option key={tool} value={tool} />
              ))}
              {PROHIBITED_HINTS.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </div>
        </div>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="dispatch-confidence">
              Confidence ({confidence})
            </label>
            <input
              id="dispatch-confidence"
              type="range"
              min={0}
              max={100}
              step={1}
              value={confidence}
              onChange={(e) => setConfidence(Number(e.target.value))}
            />
          </div>
          <div className="console-field">
            <label htmlFor="dispatch-guest">
              <input
                id="dispatch-guest"
                type="checkbox"
                checked={guestContent}
                onChange={(e) => setGuestContent(e.target.checked)}
              />{" "}
              Contains guest content
            </label>
          </div>
        </div>
        <button type="submit" className="console-btn" disabled={dispatching}>
          {dispatching ? "Dispatching…" : "Dispatch"}
        </button>
      </form>
      {result !== null ? (
        <div className="agent-outcome" role="status" aria-live="polite">
          <span className={decisionBadgeClass(result.decision)}>
            {result.decision}
          </span>
          <p className="agent-outcome__reason">{result.reason}</p>
        </div>
      ) : null}
    </section>
  );
}

// ===========================================================================
// Prompt-injection screener (§9.4 / FR-AI.7)
// ===========================================================================

function ScreenerPanel(): ReactElement {
  const [text, setText] = useState("");
  const [screening, setScreening] = useState(false);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [result, setResult] = useState<InjectionScreen | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setApiError(null);
    setResult(null);
    setScreening(true);
    try {
      const res = await apiAgentScreen(text);
      setResult(res);
    } catch {
      setApiError({
        what: "Could not screen the text.",
        why: "The request to the agent service failed.",
        next: "Check the API server is running and try again.",
      });
    } finally {
      setScreening(false);
    }
  }

  return (
    <section
      className="console-panel agent-screener"
      aria-label="Prompt-injection screener"
    >
      <h2 className="console-panel__title">Prompt-injection screener</h2>
      <p className="recon-caption">
        Guest content is treated as data, never instructions (§9.4). Screen any
        text to see whether it trips an injection heuristic and how it is
        wrapped as inert, delimited data.
      </p>
      {apiError !== null ? (
        <UserMessage message={apiError} severity="error" />
      ) : null}
      <form className="console-form" onSubmit={handleSubmit} noValidate>
        <div className="console-field">
          <label htmlFor="screen-text">Text to screen</label>
          <textarea
            id="screen-text"
            placeholder="Paste guest-supplied text here…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <button type="submit" className="console-btn" disabled={screening}>
          {screening ? "Screening…" : "Screen"}
        </button>
      </form>
      {result !== null ? (
        <div role="status" aria-live="polite">
          <p>
            Flagged:{" "}
            <span
              className={
                result.flagged
                  ? "agent-flag agent-flag--yes"
                  : "agent-flag agent-flag--no"
              }
            >
              {result.flagged ? "Yes" : "No"}
            </span>
          </p>
          <pre className="agent-sanitized" aria-label="Sanitized data output">
            {result.sanitized}
          </pre>
        </div>
      ) : null}
    </section>
  );
}

// ===========================================================================
// Action log (FR-AI.1)
// ===========================================================================

function ActionLogPanel({
  actions,
}: {
  readonly actions: readonly AgentAction[];
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Action log">
      <h2 className="console-panel__title">Action log</h2>
      <p className="recon-caption">
        The immutable audit trail of every agent decision (FR-AI.1): what was
        requested, what was decided, at what confidence, and against which model
        version.
      </p>
      {actions.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">Recent agent actions</caption>
          <thead>
            <tr>
              <th scope="col">Agent</th>
              <th scope="col">Requested action</th>
              <th scope="col">Decision</th>
              <th scope="col">Confidence</th>
              <th scope="col">Model version</th>
              <th scope="col">Guest content</th>
              <th scope="col">When</th>
            </tr>
          </thead>
          <tbody>
            {actions.map((a) => (
              <tr key={a.id}>
                <td>{a.agent}</td>
                <td>{a.requestedAction}</td>
                <td>
                  <span className={decisionBadgeClass(a.decision)}>
                    {a.decision}
                  </span>
                </td>
                <td className="agent-num">{a.confidence}</td>
                <td>{a.modelVersion ?? "—"}</td>
                <td>{a.guestContent ? "Yes" : "No"}</td>
                <td>
                  {a.createdAt !== null ? formatDateTime(a.createdAt) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">
          No actions logged yet — use the dispatch tester above to record one.
        </p>
      )}
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

interface AgentsData {
  readonly configs: AgentConfigs;
  readonly actions: readonly AgentAction[];
}

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly data: AgentsData }
  | { readonly kind: "error" };

/** Load configs (with effective states + catalogue) and the action log. */
async function loadAll(): Promise<AgentsData> {
  const [configs, actions] = await Promise.all([
    apiAgentConfigs(),
    apiAgentActions(),
  ]);
  return { configs, actions };
}

export default function AgentsScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [toggling, setToggling] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [status, setStatus] = useState<UserMessageModel | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const data = await loadAll();
      setState({ kind: "ready", data });
    } catch {
      setState({ kind: "error" });
    }
  }, []);

  useEffect(() => {
    let active = true;
    loadAll()
      .then((data) => {
        if (active) {
          setState({ kind: "ready", data });
        }
      })
      .catch(() => {
        if (active) {
          setState({ kind: "error" });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const handleToggleKillSwitch = useCallback(
    async (nextOn: boolean): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setToggling(true);
      try {
        const result = await apiAgentKillSwitch(nextOn);
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        setStatus(
          nextOn
            ? {
                what: "Kill switch engaged.",
                why: "Autonomous outbound action is halted tenant-wide. Draft generation is retained.",
                next: "Release the kill switch when you are ready to resume autonomous action.",
              }
            : {
                what: "Kill switch released.",
                why: "Agents may act again within their autonomy level and confidence thresholds.",
                next: "Monitor the action log as agents resume.",
              },
        );
        await refresh();
      } catch {
        setApiError({
          what: "Could not toggle the kill switch.",
          why: "The request to the agent service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setToggling(false);
      }
    },
    [refresh],
  );

  const handleAutonomyChange = useCallback(
    async (agent: string, level: AutonomyLevel): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setActingId(agent);
      try {
        const result = await apiAgentSetAutonomy({
          agent,
          autonomyLevel: level,
        });
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        setStatus({
          what: `Autonomy updated for ${agent}.`,
          why: `${agent} is now at "${level}" autonomy.`,
          next: "Use the dispatch tester to see how it behaves at this level.",
        });
        await refresh();
      } catch {
        setApiError({
          what: "Could not update autonomy.",
          why: "The request to the agent service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setActingId(null);
      }
    },
    [refresh],
  );

  const handleToggleEnabled = useCallback(
    async (
      agent: string,
      level: AutonomyLevel,
      nextEnabled: boolean,
    ): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setActingId(agent);
      try {
        const result = await apiAgentSetAutonomy({
          agent,
          autonomyLevel: level,
          enabled: nextEnabled,
        });
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        setStatus({
          what: `${agent} ${nextEnabled ? "enabled" : "disabled"}.`,
          why: nextEnabled
            ? `${agent} may operate within its autonomy level.`
            : `${agent} will not act until re-enabled.`,
          next: "Adjust autonomy or dispatch a test action to verify.",
        });
        await refresh();
      } catch {
        setApiError({
          what: "Could not update the agent.",
          why: "The request to the agent service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setActingId(null);
      }
    },
    [refresh],
  );

  return (
    <section aria-labelledby="agents-heading" data-console-screen="agents">
      <p className="console-page-eyebrow">Autonomy · Governance</p>
      <h1 id="agents-heading">AI Agents</h1>
      <p className="console-lede">
        You govern the five autonomous agents (SRS §9): set each agent&apos;s
        autonomy level, hold everything with the tenant-wide kill switch, and
        review every decision in the immutable action log. Prohibited actions
        are impossible by construction — an agent can only ever use its
        registered tools (§9.3).
      </p>

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading agent governance…
        </p>
      ) : null}

      {state.kind === "error" ? (
        <p className="console-empty" role="status">
          Agent governance data is unavailable right now. Try refreshing in a
          moment.
        </p>
      ) : null}

      {state.kind === "ready" ? (
        <>
          <KillSwitchBanner
            engaged={isKillSwitchEngaged(state.data.configs)}
            toggling={toggling}
            onToggle={() =>
              void handleToggleKillSwitch(
                !isKillSwitchEngaged(state.data.configs),
              )
            }
          />

          {apiError !== null ? (
            <UserMessage message={apiError} severity="error" />
          ) : null}
          {status !== null ? (
            <UserMessage message={status} severity="status" />
          ) : null}

          <AgentsPanel
            data={state.data.configs}
            onAutonomyChange={(agent, level) => {
              void handleAutonomyChange(agent, level);
            }}
            onToggleEnabled={(agent, level, nextEnabled) => {
              void handleToggleEnabled(agent, level, nextEnabled);
            }}
            actingId={actingId}
          />

          <DispatchPanel
            agents={state.data.configs.agents}
            onDispatched={() => {
              void refresh();
            }}
          />

          <ScreenerPanel />

          <ActionLogPanel actions={state.data.actions} />
        </>
      ) : null}
    </section>
  );
}
